/**
 * MatchResultCard – Collapsed: date, type pill, avatars, points (big), correct picks (small).
 * Expanded: same + match rows (FriendChallengeMatchRows).
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { View, Text, Animated, Platform } from 'react-native';
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable';
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants';
import { Image } from 'expo-image';
import { ChevronDown, Flag } from 'lucide-react-native';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import type { PickHistoryEntry } from '../../../src/friends/types/friends.types';
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types';
import { FriendAvatar } from '../shared/FriendAvatar';
import { FriendChallengeMatchRows } from './FriendChallengeMatchRows';
import { getUserName } from '../../../src/friends/helpers/getUserName.helper';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { formatFullDate } from '../../../helpers/dateFormatters.helper';
import { formatPointsDisplay } from '../../../src/friends/helpers/formatPoints.helper';
import {
  getCardTypeFromEntry,
  getCorrectPicksFromEntry,
  type CardTypeKey,
} from '../../../src/friends/helpers/matchResultCard.helper';
import { getMatchResultCardStyles } from './MatchResultCard.styles';
import { useTheme } from '../../../src/theme/useTheme';
import {
  GROUP_CHALLENGE_HISTORY_SPECTATOR_BANNER,
  GROUP_CHALLENGE_HISTORY_SPECTATOR_OUTCOME_LABEL,
  GROUP_CHALLENGE_HISTORY_SPECTATOR_SCORE_PLACEHOLDER,
} from '../../../src/friends/constants/groups.constants';
import { CHALLENGE_STATUS } from '../../../src/friends/constants/challengeStatus.constants';

const CARD_AVATAR_SIZE = 56;

const OUTCOME_LABEL = {
  user: 'VICTORY',
  friend: 'DEFEAT',
  tie: 'DRAW',
} as const;

interface MatchResultCardProps {
  data: PickHistoryEntry;
  friendName: string;
  userAvatarId?: string | null;
  userAvatarConfig?: EquippedMap | null;
  friendAvatarId?: string | null;
  friendAvatarConfig?: EquippedMap | null;
  friendFallbackUrl?: string | null;
  /** Optional content rendered at top of card (e.g. participant switcher for group challenges). */
  headerContent?: React.ReactNode;
  /** Name of the challenge creator ("You" or friend's name). */
  creatorName?: string | null;
  testID?: string;
}

function getPillStyles(
  key: CardTypeKey,
  styles: ReturnType<typeof getMatchResultCardStyles>
): { container: object; text: object } {
  const pill: Record<CardTypeKey, [object, object]> = {
    LIVE: [styles.matchResultCardPillLive, styles.matchResultCardPillTextLive],
    ACTIVE: [styles.matchResultCardPillActive, styles.matchResultCardPillTextActive],
    FINISHED: [styles.matchResultCardPillFinished, styles.matchResultCardPillTextFinished],
    CANCELLED: [styles.matchResultCardPillCancelled, styles.matchResultCardPillTextCancelled],
  };
  const [container, text] = pill[key] ?? pill.FINISHED;
  return { container: [styles.matchResultCardPill, container], text: [styles.matchResultCardPillText, text] };
}

export const MatchResultCard = React.memo<MatchResultCardProps>(function MatchResultCard({
  data,
  friendName,
  userAvatarId,
  userAvatarConfig,
  friendAvatarId,
  friendAvatarConfig,
  friendFallbackUrl,
  headerContent,
  creatorName,
  testID,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const styles = useMemo(() => getMatchResultCardStyles(theme), [theme]);
  const { user } = useSupabaseAuth();
  const userName = getUserName(user);
  const rotateAnim = useRef(new Animated.Value(0)).current;

  const isSpectatorCompleted =
    data.viewerDidNotParticipate === true && data.status === CHALLENGE_STATUS.COMPLETED;

  const { label: typeLabel, key: typeKey } = getCardTypeFromEntry(data);
  const { userCorrect, friendCorrect } = getCorrectPicksFromEntry(data);
  const pillStyles = getPillStyles(typeKey, styles);

  const outcomeLabel = isSpectatorCompleted
    ? GROUP_CHALLENGE_HISTORY_SPECTATOR_OUTCOME_LABEL
    : data.winner
      ? OUTCOME_LABEL[data.winner]
      : '–';

  const pointsText = isSpectatorCompleted
    ? `${GROUP_CHALLENGE_HISTORY_SPECTATOR_SCORE_PLACEHOLDER}:${formatPointsDisplay(data.friendPoints)}`
    : `${formatPointsDisplay(data.userPoints)}:${formatPointsDisplay(data.friendPoints)}`;

  const coinAmount = data.stakeCoins ?? 0;
  const participantCount = Math.max(data.participantCount ?? 2, 2);
  const coinLabel =
    isSpectatorCompleted || data.stakeCoins == null
      ? null
      : data.winner === 'user'
        ? `+${new Intl.NumberFormat().format(coinAmount * participantCount)}`
        : data.winner === 'friend'
          ? `-${new Intl.NumberFormat().format(coinAmount)}`
          : `0`;

  const userMood = isSpectatorCompleted
    ? 'idle'
    : data.winner === 'user'
      ? 'happy'
      : data.winner === 'friend'
        ? 'sad'
        : 'idle';
  const friendMood = isSpectatorCompleted
    ? 'idle'
    : data.winner === 'friend'
      ? 'happy'
      : data.winner === 'user'
        ? 'sad'
        : 'idle';

  const scoreColorStyle = isSpectatorCompleted
    ? styles.matchResultCardScoreDraw
    : data.winner === 'user'
      ? styles.matchResultCardScoreVictory
      : data.winner === 'friend'
        ? styles.matchResultCardScoreDefeat
        : styles.matchResultCardScoreDraw;

  useEffect(() => {
    Animated.spring(rotateAnim, {
      toValue: isExpanded ? 1 : 0,
      tension: 50,
      friction: 7,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [isExpanded, rotateAnim]);

  const rotateInterpolate = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const formatDate = (dateString: string) => formatFullDate(new Date(dateString));

  return (
    <View style={styles.matchResultCard} testID={testID || `match-result-card-${data.challengeId}`}>
      {headerContent ? <View style={styles.matchResultCardHeader}>{headerContent}</View> : null}
      {isSpectatorCompleted ? (
        <View style={styles.matchResultCardSpectatorBanner}>
          <Text style={styles.matchResultCardSpectatorBannerText}>
            {GROUP_CHALLENGE_HISTORY_SPECTATOR_BANNER}
          </Text>
        </View>
      ) : null}
      <SoundPressable
        tapSoundId={
          isExpanded ? SOUND_IDS.UI_SECTION_COLLAPSE : SOUND_IDS.UI_SECTION_EXPAND
        }
        style={({ pressed, hovered }) => [
          styles.matchResultCardPressable,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.matchResultCardPressablePressed,
          hovered && Platform.OS === 'web' && styles.matchResultCardPressableHover,
        ]}
        onPress={() => setIsExpanded((e) => !e)}
      >
        <View style={styles.matchResultCardTopRow}>
          <Text style={styles.matchResultCardDate}>{formatDate(data.date)}</Text>
          <View style={pillStyles.container}>
            <Text style={pillStyles.text}>{typeLabel}</Text>
          </View>
        </View>

        <View style={styles.matchResultCardMiddle}>
          <View style={styles.matchResultCardPlayer}>
            <FriendAvatar
              avatarId={(userAvatarId ?? undefined) as DisplayAvatarId | undefined}
              avatarConfig={userAvatarConfig}
              small={false}
              size={CARD_AVATAR_SIZE}
              nameForInitials={userName}
              mood={userMood}
              style={styles.matchResultCardAvatar}
            />
            <Text style={styles.matchResultCardPlayerName}>{userName}</Text>
            <Text style={styles.matchResultCardPts}>
              {isSpectatorCompleted ? '—' : `${userCorrect} correct`}
            </Text>
          </View>

          <View style={styles.matchResultCardCenter}>
            <Text style={[styles.matchResultCardScore, scoreColorStyle]}>{pointsText}</Text>
            <Text style={styles.matchResultCardOutcome}>{outcomeLabel}</Text>
            {coinLabel != null && (
              <View style={[
                styles.matchResultCardCoinChip,
                data.winner === 'user' && styles.matchResultCardCoinChipWin,
                data.winner === 'friend' && styles.matchResultCardCoinChipLoss,
              ]}>
                <Text style={[
                  styles.matchResultCardCoinChipText,
                  data.winner === 'user' && styles.matchResultCardCoinChipTextWin,
                  data.winner === 'friend' && styles.matchResultCardCoinChipTextLoss,
                ]}>
                  {coinLabel}
                </Text>
                <Image
                  source={IDLE_COIN_SOURCE}
                  style={styles.coinImage}
                  alt={WEB_IMAGE_ALT_DECORATIVE}
                />
              </View>
            )}
          </View>

          <View style={styles.matchResultCardPlayer}>
            <FriendAvatar
              avatarId={(friendAvatarId ?? undefined) as DisplayAvatarId | undefined}
              avatarConfig={friendAvatarConfig}
              fallbackUrl={friendFallbackUrl ?? undefined}
              small={false}
              size={CARD_AVATAR_SIZE}
              nameForInitials={friendName}
              mood={friendMood}
              style={styles.matchResultCardAvatar}
            />
            <Text style={styles.matchResultCardPlayerName}>{friendName}</Text>
            <Text style={styles.matchResultCardPts}>{friendCorrect} correct</Text>
          </View>
        </View>

        <Animated.View style={[styles.matchResultCardExpandIcon, { transform: [{ rotate: rotateInterpolate }] }]}>
          <ChevronDown size={24} color={theme.gray[500]} />
        </Animated.View>
      </SoundPressable>

      {isExpanded && data.matches.length > 0 && (
        <View style={styles.matchResultCardExpandedContent}>
          <FriendChallengeMatchRows
            matches={data.matches}
            friendName={friendName}
            testID={testID ? `${testID}-matches` : undefined}
          />
        </View>
      )}

      {creatorName != null && (
        <View style={styles.matchResultCardCreatedBy}>
          <Flag size={11} color={theme.gray[400]} />
          <Text style={styles.matchResultCardCreatedByText}>
            Created by <Text style={styles.matchResultCardCreatedByName}>{creatorName}</Text>
          </Text>
        </View>
      )}
    </View>
  );
});
